import React, { useState } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiListBold, PiXBold } from "react-icons/pi";
import LogoUploader from "./LogoUploader";
import LoginDropdown from "./LoginDropdown";

export default function SplitHeader({
  content,
  onContentChange,
  isSelected,
  defaultLogo,
}: SectionContentProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const primaryColor = content.colorPalette?.primary || "#3B82F6";
  const secondaryColor = content.colorPalette?.secondary || "#4F46E5";
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const padding = content.padding || "normal";
  const getPaddingClass = () => {
    if (padding === "compact") return "py-2";
    if (padding === "spacious") return "py-10";
    return "py-5";
  };

  const handleLogoChange = (newLogoUrl: string) => {
    if (onContentChange) {
      onContentChange({ ...content, logo: newLogoUrl });
    }
  };

  const navItemsRaw = content.navItems || [];

  return (
    <>
      <div
        className={`w-full transition-all ${isSelected ? "ring-2 ring-blue-500 relative z-20 shadow-md" : "border-b border-gray-100"}`}
        style={{
          fontFamily,
          backgroundColor: content.backgroundColor || "#ffffff",
        }}
      >
        <div
          className={`max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between ${getPaddingClass()}`}
        >
          {/* Left Side: Logo */}
          <div className="flex-shrink-0 mr-4">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-10"
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              companyName={content.companyName || "INSTACKS"}
              isEditing={!!onContentChange}
            />
          </div>

          {/* Mobile Menu Icon */}
          <div className="md:hidden">
            <button
              className="p-2 -mr-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <PiListBold className="w-6 h-6" />
            </button>
          </div>

          {/* Right Side: Nav + Actions */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {/* Navigation Items (Optional - can be empty) */}
            <nav className="flex items-center gap-6">
              {navItemsRaw.map((item: any, idx: number) => {
                const navItem = typeof item === 'string' 
                    ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, '-') } 
                    : item;
                    
                const isCustomLink = navItem.sectionId === "custom";
                const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
                const isEditing = !!onContentChange;

                return (
                  <a
                    key={idx}
                    href={isEditing ? undefined : targetHref}
                    target={isCustomLink && !isEditing ? "_blank" : undefined}
                    rel={isCustomLink ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (isEditing) {
                        e.preventDefault();
                      } else if (!isCustomLink) {
                        e.preventDefault();
                        const el = document.getElementById(navItem.sectionId);
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    <EditableText
                      value={navItem.label}
                      onChange={(val) => {
                        const newItems = [...(content.navItems || [])];
                        newItems[idx] = { ...navItem, label: val };
                        if (onContentChange)
                          onContentChange({ ...content, navItems: newItems });
                      }}
                      className="text-sm font-semibold transition-all cursor-pointer px-4 py-2 rounded-full uppercase tracking-wide border border-transparent hover:border-gray-200 hover:bg-gray-50 hover:shadow-sm"
                      style={{
                        color: primaryColor,
                      }}
                      as="span"
                      placeholder="NAV ITEM"
                    />
                  </a>
                );
              })}
            </nav>

            {/* Separator */}
            {navItemsRaw.length > 0 && (
              <div className="h-6 w-px bg-gray-200 hidden lg:block"></div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <LoginDropdown variant="text" primaryColor={primaryColor} />

              <button
                className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
              >
                <EditableText
                  value={content.ctaText || "Get Started"}
                  onChange={(val) =>
                    onContentChange &&
                    onContentChange({ ...content, ctaText: val })
                  }
                  className="text-white"
                  as="span"
                  placeholder="CTA Text"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col md:hidden" style={{ fontFamily }}>
          <div className="flex items-center justify-between p-4 sm:p-8 border-b border-gray-100">
            <LogoUploader
              currentLogo={content.logo}
              defaultLogo={defaultLogo}
              onLogoChange={handleLogoChange}
              heightClass="h-8"
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              companyName={content.companyName || "INSTACKS"}
              isEditing={!!onContentChange}
            />
            <button
              className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <PiXBold className="w-6 h-6" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
            {navItemsRaw.length > 0 && (
              <nav className="flex flex-col gap-4">
                {navItemsRaw.map((item: any, idx: number) => {
                  const navItem = typeof item === 'string' 
                      ? { label: item, sectionId: item.toLowerCase().replace(/[^a-z0-9]+/g, '-') } 
                      : item;
                      
                  const isCustomLink = navItem.sectionId === "custom";
                  const targetHref = isCustomLink ? navItem.url : `#${navItem.sectionId}`;
                  const isEditing = !!onContentChange;

                  return (
                    <a
                      key={idx}
                      href={isEditing ? undefined : targetHref}
                      target={isCustomLink && !isEditing ? "_blank" : undefined}
                      rel={isCustomLink ? "noopener noreferrer" : undefined}
                      className="text-lg font-semibold border-b border-gray-50 pb-4"
                      style={{ color: primaryColor }}
                      onClick={(e) => {
                        if (isEditing) {
                          e.preventDefault();
                        } else if (!isCustomLink) {
                          e.preventDefault();
                          setIsMobileMenuOpen(false);
                          const el = document.getElementById(navItem.sectionId);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                          }
                        } else {
                          setIsMobileMenuOpen(false);
                        }
                      }}
                    >
                      {navItem.label}
                    </a>
                  );
                })}
              </nav>
            )}
            <div className="mt-auto pt-8 flex flex-col gap-4 border-t border-gray-100">
              <div className="w-full text-center py-2 border border-gray-200 rounded-lg font-semibold" style={{ color: primaryColor }}>
                <LoginDropdown variant="text" primaryColor={primaryColor} />
              </div>
              <button
                className="w-full rounded-lg px-6 py-3 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {content.ctaText || "Get Started"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
